import { Routes, UrlMatchResult, UrlMatcher, UrlSegment } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { alreadyLoggedInGuard } from './core/guards/already-logged-in.guard';
import { cancelingProcessesGuard } from './core/guards/canceling-processes.guard';

const supportedLocales = ['en', 'es', 'pt'];

const localeMatcher: UrlMatcher = (segments: UrlSegment[]): UrlMatchResult | null => {
    if (segments.length && supportedLocales.includes(segments[0].path)) {
        return { consumed: [segments[0]] };
    }
    return null;
};

const actorTypeRoutes: Routes = [
    {
        path: 'create',
        loadComponent: () => import('./private/commodity/pages/create-actor-type-page/create-actor-type-page.component').then(c => c.CreateActorTypePageComponent),
        canDeactivate: [cancelingProcessesGuard]
    },
    {
        path: 'edit/:idActorType',
        loadComponent: () => import('./private/commodity/pages/edit-actor-type-page/edit-actor-type-page.component').then(c => c.EditActorTypePageComponent),
        canDeactivate: [cancelingProcessesGuard]
    },
    {
        path: 'list',
        loadComponent: () => import('./private/commodity/pages/actor-type-list-page/actor-type-list-page.component').then(c => c.ActorTypeListPageComponent)
    },
];

const producerTypeRoutes: Routes = [
    {
        path: 'list',
        redirectTo: '/commodities/actor-type/list',
        pathMatch: 'full'
    },
    {
        path: 'create',
        loadComponent: () => import('./private/commodity/pages/create-producer-type-page/create-producer-type-page.component').then(c => c.CreateProducerTypePageComponent),
        canDeactivate: [cancelingProcessesGuard]
    },
];
const producerTypeAliasRoutes: Routes = [
    {
        path: 'create',
        loadComponent: () => import('./private/commodity/pages/create-producer-type-page/create-producer-type-page.component').then(c => c.CreateProducerTypePageComponent),
        canDeactivate: [cancelingProcessesGuard]
    },
];
const dependencyRulesRoutes: Routes = [
    {
        path: 'create',
        loadComponent: () => import('./private/traceability/pages/create-dependency-page/create-dependency-page.component').then(c => c.CreateDependencyPageComponent),
        canDeactivate: [cancelingProcessesGuard]
    },
    {
        path: 'edit',
        loadComponent: () => import('./private/traceability/pages/edit-dependency-page/edit-dependency-page.component').then(c => c.EditDependencyPageComponent),
        canDeactivate: [cancelingProcessesGuard]
    },
    {
        path: 'list',
        loadComponent: () => import('./private/traceability/pages/dependency-rule-tree-page/tree-dependency-rule-page.component').then(c => c.TreeDependencyRulePageComponent)
    },
];

const baseRoutes: Routes = [
    {
        path: 'download',
        loadComponent: () => import('./shared/components/download/download.component').then(c => c.DownloadComponent)
    },
    {
        path: 'login',
        loadComponent: () => import('./public/auth/pages/login-page/login-page.component').then(c => c.LoginPageComponent),
        canActivate: [alreadyLoggedInGuard],
        children: [
            {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./public/auth/components/login-form/login-form.component').then(c => c.LoginFormComponent)
            },
            {
                path: 'forgot-password',
                pathMatch: 'full',
                loadComponent: () => import('./public/auth/components/password-recovery-form/password-recovery-form.component').then(c => c.PasswordRecoveryFormComponent)
            },
            {
                path: 'recover-security-question',
                pathMatch: 'full',
                loadComponent: () => import('./public/auth/components/recover-security-question/recover-security-question.component').then(c => c.RecoverSecurityQuestionComponent)
            },
            {
                path: 'reset-password/:otp',
                pathMatch: 'full',
                loadComponent: () => import('./public/auth/pages/password-reset-page/password-reset-page.component').then(c => c.PasswordResetPageComponent)
            }
        ]
    },
    {
        path: '',
        loadComponent: () => import('./layout/dashboard/dashboard.component').then(c => c.DashboardComponent),
        canActivate: [authGuard],
        children: [
            // Home
            {
                path: '',
                pathMatch: 'full',
                loadComponent: () => import('./private/home/pages/home-page/home-page.component').then(c => c.HomePageComponent)
            },

            // Users Routes
            {
                path: 'users',
                children: [
                    {
                        path: 'create',
                        loadComponent: () => import('./private/users/pages/create-user-page/create-user-page.component').then(c => c.CreateUserPageComponent),
                        canDeactivate: [cancelingProcessesGuard]
                    },
                    {
                        path: 'edit/:idUser',
                        loadComponent: () => import('./private/users/pages/edit-user/edit-user.component').then(c => c.EditUserComponent),
                        canDeactivate: [cancelingProcessesGuard]
                    },
                    {
                        path: 'list',
                        loadComponent: () => import('./private/users/pages/user-list/user-list.component').then(c => c.UserListComponent)
                    },
                    {
                        path: 'me',
                        loadComponent: () => import('./private/users/pages/my-user-page/my-user-page.component').then(c => c.MyUserPageComponent),
                        canDeactivate: [cancelingProcessesGuard]
                    }
                ]
            },
            // Commodities Routes
            {
                path: 'commodities',
                children: [
                    {
                        path: 'list',
                        loadComponent: () => import('./private/commodity/pages/commodity-list-page/commodity-list-page.component').then(c => c.CommodityListPageComponent)
                    },
                    {
                        path: 'update/:idCommodity',
                        loadComponent: () => import('./private/commodity/pages/edit-commodity-page/edit-commodity-page.component').then(c => c.EditCommodityPageComponent)
                    },
                    {
                        path: 'create',
                        loadComponent: () => import('./private/commodity/pages/create-commodity-page/create-commodity-page.component').then(c => c.CreateCommodityPageComponent)
                    },                    {
                        path: 'actor-type',
                        children: actorTypeRoutes
                    },
                    {
                        path: 'producer-types',
                        children: producerTypeRoutes
                    },
                    {
                        path: 'producer-type',
                        children: producerTypeAliasRoutes
                    },

                    // scope Type Routes

                    {
                        path: 'scope-types',
                        children: [
                            {
                                path: 'list',
                                loadComponent: () => import('./private/commodity/pages/scope-type-list-page/scope-type-list-page.component').then(c => c.ScopeTypeListPageComponent)
                            },
                            {
                                path: 'create',
                                loadComponent: () => import('./private/commodity/pages/create-scope-type-page/create-scope-type-page.component').then(c => c.CreateScopeTypePageComponent),
                                canDeactivate: [cancelingProcessesGuard]
                            },
                            {
                                path: 'update/:idScopeType',
                                loadComponent: () => import('./private/commodity/pages/edit-scope-type-page/edit-scope-type-page.component').then(c => c.EditScopeTypePageComponent),
                                canDeactivate: [cancelingProcessesGuard]
                            }
                        ]
                    },
                    {
                        path: 'dependency-rules',
                        children: dependencyRulesRoutes
                    }
                ]
            },

            // Companies Routes
            {
                path: 'companies',
                children: [
                    {
                        path: 'create',
                        loadComponent: () => import('./private/companies/pages/create-company-page/create-company-page.component').then(c => c.CreateCompanyPageComponent),
                        canDeactivate: [cancelingProcessesGuard]
                    },
                    {
                        path: 'edit/:idCompany',
                        loadComponent: () => import('./private/companies/pages/edit-company-page/edit-company-page.component').then(c => c.EditCompanyPageComponent),
                        canDeactivate: [cancelingProcessesGuard]
                    },
                    {
                        path: 'list',
                        loadComponent: () => import('./private/companies/pages/company-list-page/company-list-page.component').then(c => c.CompanyListPageComponent)
                    },


                    // Parent Company Routes
                    {
                        path: 'parent-company',
                        children: [
                            {
                                path: 'create',
                                loadComponent: () => import('./private/companies/pages/create-company-page/create-company-page.component').then(c => c.CreateCompanyPageComponent),
                                canDeactivate: [cancelingProcessesGuard]
                            },
                            {
                                path: 'edit/:idCompany',
                                loadComponent: () => import('./private/companies/pages/edit-company-page/edit-company-page.component').then(c => c.EditCompanyPageComponent),
                                canDeactivate: [cancelingProcessesGuard]
                            },
                            {
                                path: 'list',
                                loadComponent: () => import('./private/companies/pages/parent-company-list-page/parent-company-list-page.component').then(c => c.ParentCompanyListPageComponent)
                            }
                        ]
                    },

                    // Holding Group Routes
                    {
                        path: 'holding-group',
                        children: [
                            {
                                path: 'create',
                                loadComponent: () => import('./private/companies/pages/create-holding-group-page/create-holding-group-page.component').then(c => c.CreateHoldingGroupPageComponent),
                                canDeactivate: [cancelingProcessesGuard]
                            },
                            {
                                path: 'edit/:idHoldingGroup',
                                loadComponent: () => import('./private/companies/pages/edit-holding-group-page/edit-holding-group-page.component').then(c => c.EditHoldingGroupPageComponent),
                                canDeactivate: [cancelingProcessesGuard]
                            },
                            {
                                path: 'list',
                                loadComponent: () => import('./private/companies/pages/holding-group-list-page/holding-group-list-page.component').then(c => c.HoldingGroupListPageComponent)
                            }
                        ]
                    },

                    // Certification Routes
                    {
                        path: 'certification',
                        children: [
                            {
                                path: 'create',
                                loadComponent: () => import('./private/companies/pages/create-certification-page/create-certification-page.component').then(c => c.CreateCertificationPageComponent),
                                canDeactivate: [cancelingProcessesGuard]
                            },
                            {
                                path: 'edit/:idCertificate',
                                loadComponent: () => import('./private/companies/pages/edit-certification-page/edit-certification-page.component').then(c => c.EditCertificationPageComponent),
                                canDeactivate: [cancelingProcessesGuard]
                            }
                        ]
                    },
                    {
                        path: 'certifications/list',
                        loadComponent: () => import('./private/companies/pages/certification-list-page/certification-list-page.component').then(c => c.CertificationListPageComponent)
                    },

                ]
            },

            // Questions Routes
            {
                path: 'questions',
                children: [
                    {
                        path: 'list',
                        loadComponent: () => import('./private/questions/pages/question-list-page/question-list-page.component').then(c => c.QuestionListPageComponent)
                    },
                    {
                        path: 'create',
                        loadComponent: () => import('./private/questions/pages/create-question-page/create-question-page.component').then(c => c.CreateQuestionPageComponent)
                    },
                    {
                        path: 'list/edit/:idQuestion',
                        loadComponent: () => import('./private/questions/pages/edit-question-page/edit-question-page.component').then(c => c.EditQuestionPageComponent)
                    },

                    // Tags Routes
                    {
                        path: 'tags',
                        children: [
                            {
                                path: '',
                                pathMatch: 'full',
                                loadComponent: () => import('./private/questions/pages/tag-list-page/tag-list-page.component').then(c => c.TagListPageComponent)
                            },
                            {
                                path: 'create',
                                loadComponent: () => import('./private/questions/pages/create-tag-page/create-tag-page.component').then(c => c.CreateTagPageComponent)
                            },
                            {
                                path: 'edit/:tagId',
                                loadComponent: () => import('./private/questions/pages/update-tag-page/update-tag-page.component').then(c => c.UpdateTagPageComponent)
                            }
                        ]
                    }
                ]
            },

            // Forms Routes
            {
                path: 'forms',
                children: [
                    {
                        path: 'create',
                        loadComponent: () => import('./private/forms/pages/create-form-page/create-form-page.component').then(c => c.CreateFormPageComponent)
                    },
                    {
                        path: 'manage',
                        loadComponent: () => import('./private/forms/pages/form-list-page/form-list-page.component').then(c => c.TemplateListPageComponent)
                    },
                    {
                        path: 'edit/:idForm',
                        loadComponent: () => import('./private/forms/pages/edit-form-page/edit-form-page.component').then(c => c.EditFormPageComponent)
                    },
                    {
                        path: 'preview/:idForm',
                        loadComponent: () => import('./private/forms/pages/preview-form-page/preview-form-page.component').then(c => c.PreviewFormPageComponent)
                    },
                    {
                        path: 'results',
                        loadComponent: () => import('./private/forms/pages/view-form-result-pages/view-form-result-pages.component').then(c => c.ViewFormResultPagesComponent)
                    },
                    {
                        path: 'results/view/:clientId/:supplierIdresults/:idForm',
                        loadComponent: () => import('./private/forms/pages/view-results-page/view-results-page.component').then(c => c.ViewResultsPageComponent)
                    },
                    {
                        path: 'my-forms',
                        loadComponent: () => import('./private/forms/pages/fillable-form-list-page/fillable-form-list-page.component').then(c => c.FillableFormListPageComponent)
                    },
                    {
                        path: 'my-forms/view-form/:idForm',
                        loadComponent: () => import('./private/forms/pages/answer-form-page/answer-form-page.component').then(c => c.AnswerFormPageComponent),
                        canDeactivate: [cancelingProcessesGuard]
                    },
                    {
                        path: 'internal-assessment/view-form/:idForm',
                        loadComponent: () => import('./private/forms/pages/answer-form-page/answer-form-page.component').then(c => c.AnswerFormPageComponent),
                    },
                    {
                        path: 'internal-assessment',
                        loadComponent: () => import('./private/forms/pages/internal-assessment-page/internal-assessment-page.component').then(c => c.InternalAssessmentPageComponent)
                    },
                    {
                        path: 'internal-assessment/:clientId/:idForm/:supplierIdresults/view-form',
                        loadComponent: () => import('./private/forms/pages/answer-form-page/answer-form-page.component').then(c => c.AnswerFormPageComponent),
                        canDeactivate: [cancelingProcessesGuard]
                    },
                    {
                        path: 'validate-form-list',
                        loadComponent: () => import('./private/forms/pages/validate-form-list-page/validate-form-list-page.component').then(c => c.ValidateFormListPageComponent)
                    },
                    {
                        path: 'validate/:idForm',
                        loadComponent: () => import('./private/forms/pages/view-results-page/view-results-page.component').then(c => c.ViewResultsPageComponent)
                    },
                    {
                        path: 'my-forms/results/:idForm',
                        loadComponent: () => import('./private/forms/pages/view-results-page/view-results-page.component').then(c => c.ViewResultsPageComponent)
                    },
                    {
                        path: 'internal-assessment/results/:idForm',
                        loadComponent: () => import('./private/forms/pages/view-results-page/view-results-page.component').then(c => c.ViewResultsPageComponent)
                    },
                    {
                        path: 'results/:idForm',
                        loadComponent: () => import('./private/forms/pages/view-results-page/view-results-page.component').then(c => c.ViewResultsPageComponent)
                    },

                    // Assignment Routes
                    {
                        path: 'assign',
                        children: [
                            {
                                path: 'supplier',
                                loadComponent: () => import('./private/forms/pages/assign-supplier-form-page/assign-supplier-form-page.component').then(c => c.AssignSupplierFormPageComponent)
                            },
                            {
                                path: 'implementer',
                                loadComponent: () => import('./private/forms/pages/assign-implementer-page/assign-implementer-page.component').then(c => c.AssignImplementerPageComponent)
                            },
                            {
                                path: 'client/:idForm',
                                loadComponent: () => import('./private/forms/pages/assign-client-form-page/assign-client-form-page.component').then(c => c.AssignClientFormPageComponent)
                            },
                            {
                                path: 'validator',
                                loadComponent: () => import('./private/forms/pages/assign-validator-page/assign-validator-page.component').then(c => c.AssignValidatorPageComponent)
                            },
                            {
                                path: 'viewer',
                                loadComponent: () => import('./private/forms/pages/assign-viewer-page/assign-viewer-page.component').then(c => c.AssignViewerPageComponent)
                            }
                        ]
                    },

                    // Implementer Routes
                    {
                        path: 'implementer',
                        children: [
                            {
                                path: 'view-form/:idForm',
                                loadComponent: () => import('./private/forms/pages/answer-form-page/answer-form-page.component').then(c => c.AnswerFormPageComponent)
                            }
                        ]
                    }
                ]
            },

            // Supply Base Routes
            {
                path: 'supply-base',
                children: [
                    {
                        path: 'create',
                        loadComponent: () => import('./private/supply-base/pages/create-supply-base-page/create-supply-base-page.component').then(c => c.CreateSupplyBasePageComponent)
                    },
                    {
                        path: 'list',
                        loadComponent: () => import('./private/supply-base/pages/supply-base-list-page/list-supply-base-page.component').then(c => c.ListSupplyBasePageComponent)
                    }
                ]
            },  
            {
                path: 'tickets',
                children: [
                    {
                        path: 'list',
                        title: $localize`List Tickets`,
                        loadComponent: () => import('./private/helper/pages/ticket-list-page/ticket-list-page.component').then(c => c.TicketListPageComponent)
                    },
                    {
                        path: 'statistics',
                        title: 'Ticket Statistics',
                        loadComponent: () => import('./private/helper/pages/ticket-statistics-page/ticket-statistics-page.component').then(c => c.TicketStatisticsPageComponent)
                    },
                    {
                        path: 'nps-statistics',
                        title: 'NPS Statistics',
                        loadComponent: () => import('./private/helper/pages/nps-statistics-page/nps-statistics-page.component').then(c => c.NpsStatisticsPageComponent)
                    },
                    {
                        path: 'chat/:number_tickets',
                        title: $localize`Ticket Chat`,
                        loadComponent: () => import('./private/helper/pages/ticket-chat-page/ticket-chat-page.component').then(c => c.TicketChatPageComponent)
                    }
                ]
            },
            {
                path: 'traceability',
                children: [
                    {
                        path: 'supplybase/list',
                        title: $localize`Traceability Supply Base`,
                        loadComponent: () => import('./private/traceability/pages/supply-list/supply-list.component').then(c => c.SupplyListComponent)
                    },
                    {
                        path: 'supplybase/producer/:relationId',
                        title: $localize`:@@producer-record-page-title:Producer record`,
                        loadComponent: () => import('./private/traceability/pages/producer-record/producer-record.component').then(c => c.ProducerRecordComponent)
                    },
                    {
                        path: 'volume-load',
                        title: $localize`:@@volume-access-title:Supply Chain Volumes`,
                        loadComponent: () => import('./private/traceability/pages/volume-load-page/volume-load-page.component').then(c => c.VolumeLoadPageComponent)
                    },
                    {
                        path: 'received-requests',
                        title: $localize`:@@received-requests-route-title:Traceability requests`,
                        loadComponent: () => import('./private/traceability/pages/received-requests-page/received-requests-page.component').then(c => c.ReceivedRequestsPageComponent)
                    },
                    {
                        path: 'geo-viewer',
                        title: $localize`:@@geo-panel-title:Geoviewer`,
                        loadComponent: () => import('./private/traceability/pages/geo-viewer/geo-viewer.component').then(c => c.GeoViewerComponent)
                    },
                    {
                        path: 'volume-summary/:reportId',
                        title: $localize`:@@volume-summary-route-title:Volume summary`,
                        loadComponent: () => import('./private/traceability/pages/volume-summary-page/volume-summary-page.component').then(c => c.VolumeSummaryPageComponent)
                    },
                    {
                        path: 'statistics',
                        title: 'Ticket Statistics',
                        loadComponent: () => import('./private/helper/pages/ticket-statistics-page/ticket-statistics-page.component').then(c => c.TicketStatisticsPageComponent)
                    },
                    {
                        path: 'chat/:number_tickets',
                        title: $localize`Ticket Chat`,
                        loadComponent: () => import('./private/helper/pages/ticket-chat-page/ticket-chat-page.component').then(c => c.TicketChatPageComponent)
                    }
                ]
            }
        ]
    },
];

export const routes: Routes = [
    {
        matcher: localeMatcher,
        children: baseRoutes
    },
    ...baseRoutes,
    { path: '**', loadComponent: () => import('./shared/components/page-not-found/page-not-found.component').then(c => c.PageNotFoundComponent) }
];





